import { cn } from '@askable/ui/lib/utils';
import { Inbox } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'shared-utils';
import { useQuery, useSubscription } from 'urql';

import { UnreadNotificationsCountByTeam } from 'components/common/MainNavSidebar/components/Notifications/data/UnreadNotificationsCountByTeam.query';
import { UnreadNotificationsCountByTeamSubscription } from 'components/common/MainNavSidebar/components/Notifications/data/UnreadNotificationsCountByTeam.subscription';

import { menuItemClass, menuItemCollapsedClass } from '../MenuItem';
import { ResponsiveMenu } from '../ResponsiveMenu';
import { UnreadBadge } from '../UnreadBadge';

import { DesktopNotificationsContent, MobileNotificationsContent } from './NotificationsContent';

export const Notifications = ({ teamId, isCollapsed }: { teamId: string; isCollapsed: boolean }) => {
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const isMobile = useMediaQuery('(max-width: 639px)');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [{ data: notificationData }] = useQuery({
    query: UnreadNotificationsCountByTeam,
    variables: {
      _team_id: teamId,
    },
    pause: !teamId,
  });

  // Real-time unread count updates
  const [{ data: subscriptionData }] = useSubscription(
    {
      query: UnreadNotificationsCountByTeamSubscription,
      variables: { _team_id: teamId },
      pause: !teamId,
    },
    (prev, response) => {
      if (response?.unreadNotificationsCountByTeam) {
        return response;
      }
      return prev || { unreadNotificationsCountByTeam: [] };
    },
  );

  const unreadCount =
    subscriptionData?.unreadNotificationsCountByTeam?.[0]?.unread ??
    notificationData?.unreadNotificationsCountByTeam?.[0]?.unread ??
    0;

  const button = (
    <button
      className={cn(
        menuItemClass,
        'justify-between aria-expanded:bg-foreground/[0.065] aria-expanded:text-foreground',
        {
          // Collapsed, the row must not clip: its hit area extends past it.
          'overflow-hidden': !isCollapsed,
          [menuItemCollapsedClass]: isCollapsed,
        },
      )}
    >
      {/* This row nests its icon and label a level deeper than the others, so the
          collapsed gap-0 has to be repeated here or the dead gap pushes the icon
          off the line the rest of the rail sits on. */}
      <div className={cn('flex items-center gap-2', { 'gap-0': isCollapsed })}>
        <div className="shrink-0 p-[2px]">
          <Inbox className="size-4" />
        </div>
        <div
          className={cn('truncate whitespace-nowrap opacity-100 transition-opacity duration-150', {
            'w-0 opacity-0': isCollapsed,
          })}
        >
          {t('sections.navigation.notifications')}
        </div>
      </div>

      <UnreadBadge isCollapsed={isCollapsed} unreadCount={unreadCount} />

      {!isCollapsed && unreadCount > 0 ? (
        <div className="min-w-[1.25rem] rounded-full bg-chart-1 p-1 text-center text-xs font-semibold leading-none text-lightest">
          {unreadCount > 99 ? '99+' : unreadCount}
        </div>
      ) : null}
    </button>
  );

  return (
    <ResponsiveMenu
      id="notifications-dropdown"
      trigger={button}
      title={t('sections.navigation.notifications')}
      isCollapsed={isCollapsed}
    >
      {isMounted && isMobile ? (
        <MobileNotificationsContent teamId={teamId} />
      ) : (
        <DesktopNotificationsContent teamId={teamId} />
      )}
    </ResponsiveMenu>
  );
};
